/**
 * Stripe return flow coordination.
 * Single source of truth for "stripe flow in progress" and "handled" state
 * so dialog message, dialog close, and focus/visibility paths don't double-refresh.
 */

const STRIPE_FLOW_TTL_MS = 5 * 60 * 1000;
const MIN_REFRESH_INTERVAL_MS = 2000;

let stripeFlowInProgressUntil = 0;
let stripeFlowHandled = false;
let lastStripeRefreshTs = 0;

/**
 * Call before opening Stripe (checkout or portal). Sets TTL and clears handled.
 */
export function markStripeFlowStarted(_reason: string): void {
  stripeFlowInProgressUntil = Date.now() + STRIPE_FLOW_TTL_MS;
  stripeFlowHandled = false;
}

/**
 * Call after successfully handling a stripe return (e.g. message received and refresh done).
 * Prevents dialog-close and focus paths from triggering another refresh.
 */
export function markStripeFlowHandled(_reason: string): void {
  stripeFlowHandled = true;
  stripeFlowInProgressUntil = 0;
}

/**
 * Whether the stripe return flow has already been handled (message received or refresh done).
 */
export function isStripeFlowHandled(): boolean {
  return stripeFlowHandled;
}

/**
 * Whether we should run a refresh when the task pane regains focus/visibility.
 */
export function shouldRefreshAfterFocus(): boolean {
  if (stripeFlowHandled) return false;
  if (stripeFlowInProgressUntil <= 0 || Date.now() >= stripeFlowInProgressUntil) return false;
  if (Date.now() - lastStripeRefreshTs < MIN_REFRESH_INTERVAL_MS) return false;
  return true;
}

/**
 * Record that a stripe-triggered refresh ran (for debouncing).
 */
export function recordStripeRefresh(): void {
  lastStripeRefreshTs = Date.now();
}

/**
 * Milliseconds since last stripe-triggered refresh (for dialog-close debounce).
 * Returns Infinity if no refresh has been recorded yet.
 */
export function getMillisSinceLastStripeRefresh(): number {
  if (lastStripeRefreshTs <= 0) return Infinity;
  return Date.now() - lastStripeRefreshTs;
}

/**
 * If conditions are met, run the refresh and record it.
 */
export async function refreshAfterFocusIfNeeded(
  reason: string,
  refreshFn: (reason: string) => Promise<void>
): Promise<void> {
  if (!shouldRefreshAfterFocus()) return;
  lastStripeRefreshTs = Date.now();
  await refreshFn(reason);
}
